/**
 * Permission Engine - Company Permissions
 *
 * Handles company-specific permission evaluation
 */

import {
  UserContext,
  PermissionRequirement,
  PermissionResult,
  CompanyContract,
  PermissionAction
} from '../types';
import { createSuccessResult, createErrorResult } from './validation';

/**
 * Evaluate company-specific permissions
 */
export function evaluateCompanyPermission(
  user: UserContext,
  requirement: PermissionRequirement,
  companyContracts: CompanyContract[]
): PermissionResult {
  const { action, resourceId, companyScope } = requirement;

  // Company Admins can manage their own company
  if (user.app_role === 'COMPANY_ADMIN' && user.company_id) {
    // Check if accessing own company
    if (resourceId === user.company_id || companyScope === user.company_id) {
      switch (action) {
        case PermissionAction.VIEW:
        case PermissionAction.EDIT:
          return createSuccessResult('COMPANY_ADMIN_OWN_COMPANY', 'Company admin can manage own company');
        case PermissionAction.DELETE:
          return createErrorResult('COMPANY_DELETION_RESTRICTED', 'Company deletion requires system admin');
        default:
          return createErrorResult('UNSUPPORTED_ACTION', `Action ${action} not supported for company`);
      }
    }

    // Check if accessing contracted company
    if (resourceId) {
      const hasContract = companyContracts.some(contract =>
        (contract.company_one_id === user.company_id && contract.company_two_id === resourceId) ||
        (contract.company_two_id === user.company_id && contract.company_one_id === resourceId)
      );

      if (hasContract) {
        if (action === PermissionAction.VIEW) {
          return createSuccessResult('CONTRACT_COMPANY_VIEW', 'Can view contracted company');
        }
        return createErrorResult('LIMITED_CONTRACT_ACCESS', 'Limited access to contracted companies');
      }
    }

    return createErrorResult('NO_COMPANY_ACCESS', 'No access to this company');
  }

  // Regular users can view their own company
  if (user.company_id && (resourceId === user.company_id || companyScope === user.company_id)) {
    if (action === PermissionAction.VIEW) {
      return createSuccessResult('OWN_COMPANY_VIEW', 'User can view own company');
    }
    return createErrorResult('LIMITED_COMPANY_ACCESS', 'Users cannot modify company data');
  }

  return createErrorResult('INSUFFICIENT_COMPANY_PERMISSIONS', 'Insufficient permissions for company access');
}

/**
 * Evaluate contract-based permissions between companies
 */
export function evaluateContractPermission(
  user: UserContext,
  requirement: PermissionRequirement,
  companyContracts: CompanyContract[]
): PermissionResult {
  const { resourceId } = requirement;

  if (!user.company_id || !resourceId) {
    return createErrorResult('MISSING_CONTEXT', 'Company context required for contract evaluation');
  }

  const hasActiveContract = companyContracts.some(contract =>
    !contract.is_deleted &&
    ((contract.company_one_id === user.company_id && contract.company_two_id === resourceId) ||
     (contract.company_two_id === user.company_id && contract.company_one_id === resourceId))
  );

  if (hasActiveContract) {
    return createSuccessResult('ACTIVE_CONTRACT', 'Active contract exists between companies');
  }

  return createErrorResult('NO_CONTRACT', 'No active contract between companies');
}